import React, { useState } from 'react';
import { APITypes } from '../../../../../primary-record-types';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { getIntl } from '../../../../../utils/intl';
import getName from '../../../../../utils/getName';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { View, Pressable } from 'react-native';
import IconButton from '../../../../v2BaseComponents/IconButton';
import FileDetailsModal from './FileDetailsModal';
import Icon from '../../../../v2BaseComponents/Icon';
import { usePromptButton } from '../../../../shared/PromptButton';
import Markdown from 'react-native-markdown-display';
import { Text as RNText } from 'react-native';

const intl = getIntl('profile-file-details', {
    summary: 'Summary',
    userNoLongerExists: 'User no longer exists',
    uploadedBy: 'Uploaded by',
    aiGeneratedDisclaimer: 'Summary generated by AI',
    disclaimerTitle: 'About this content',
    disclaimerContent:
        'This summary has been generated by an artificial intelligence language model. While we strive for accuracy and quality, please note that the information provided may not be entirely error-free or up-to-date. We recommend independently verifying the content and consulting with professionals for specific advice or information. We do not assume any responsibility or liability for the use or interpretation of this content.',
});

export type FileDetailsOwnProps = {
    file: APITypes.medicalProfileFilesService.MedicalProfileFileListItem;
    isNoteFile: boolean;
    fileExtension: string;
    profileId: string;
};

export type FileDetailsProps = FileDetailsOwnProps;

const FileDetails: React.FC<FileDetailsProps> = ({ file, fileExtension }) => {
    const [isInfoPopupOpen, setIsInfoPopupOpen] = useState(false);

    // AI Disclaimer Prompt
    const { onPress: showAIDisclaimer, Prompt: AIDisclaimerPrompt } = usePromptButton({
        renderPromptContents: () => <Text variant="body-1">{intl('disclaimerContent')}</Text>,
        promptTitle: intl('disclaimerTitle'),
    });

    return (
        <>
            <MainContentContainer testID="file-screen-root">
                <ContentStack>
                    {/* File Title - Bold and truncated to one line */}
                    <RNText numberOfLines={1} ellipsizeMode="tail">
                        <Text variant="h4" style={{ fontWeight: 'bold' }}>
                            {file.fileName}
                        </Text>
                    </RNText>

                    {/* Uploaded by Section with Info Icon */}
                    <Stack horizontal align="center" space={0}>
                        <Text variant="body-2">
                            {intl('uploadedBy')}{' '}
                            {file.uploadUser ? getName(file.uploadUser) : intl('userNoLongerExists')}
                        </Text>
                        <IconButton
                            icon="Info"
                            variant="minimal"
                            onPress={() => setIsInfoPopupOpen(true)}
                            customSize="chat-button-icon"
                            testID="file-details-info-button"
                            style={{
                                cursor: 'pointer',
                                width: 18,
                                height: 18,
                                borderRadius: 9,
                                paddingLeft: 5,
                                paddingBottom: 5,
                            }}
                        />
                    </Stack>

                    {/* Divider - Only show if summary exists */}
                    {file.summary ? (
                        <View
                            style={{
                                height: 1,
                                backgroundColor: '#E5E5E5',
                                width: '100%',
                                marginVertical: 8,
                            }}
                        />
                    ) : null}

                    {/* Summary Section with AI Disclaimer */}
                    {file.summary ? (
                        <Stack vertical fullWidth space={12}>
                            <Markdown
                                style={{
                                    body: { fontSize: 16, lineHeight: 24, color: '#000' },
                                    heading1: { fontSize: 24, fontWeight: 'bold', marginTop: 12, marginBottom: 8 },
                                    heading2: { fontSize: 20, fontWeight: 'bold', marginTop: 10, marginBottom: 6 },
                                    paragraph: { marginBottom: 8 },
                                    bullet_list: { marginBottom: 8 },
                                    ordered_list: { marginBottom: 8 },
                                    list_item: { marginBottom: 4 },
                                    strong: { fontWeight: 'bold' },
                                }}
                            >
                                {file.summary}
                            </Markdown>

                            {/* AI Disclaimer Badge */}
                            <Pressable
                                onPress={showAIDisclaimer}
                                style={{
                                    backgroundColor: '#F5F5F5',
                                    paddingVertical: 6,
                                    paddingHorizontal: 10,
                                    borderRadius: 6,
                                    alignSelf: 'flex-start',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <Icon name="Sparkle" size="minimal-button-icon" colorVariant="Dark-Text" />
                                <Text variant="body-2" style={{ fontSize: 12 }}>
                                    {intl('aiGeneratedDisclaimer')}
                                </Text>
                            </Pressable>
                        </Stack>
                    ) : null}
                </ContentStack>
            </MainContentContainer>

            {/* File Details Modal */}
            <FileDetailsModal
                file={file}
                fileExtension={fileExtension}
                isVisible={isInfoPopupOpen}
                onClose={() => setIsInfoPopupOpen(false)}
            />

            {/* AI Disclaimer Prompt */}
            {AIDisclaimerPrompt}
        </>
    );
};

export default React.memo(FileDetails);
